/**
 * CreatePieceworkRequest.jsx — FULL REPLACEMENT
 *
 * Worker picker for privileged roles (admin / project_manager / foreman):
 *   - Fetches pieceworkers from /auth/users?role=worker with fallback to
 *     /mobile/users/pieceworkers.
 *   - effectiveWorkerId / effectiveWorkerRate / effectiveWorkerName derived
 *     from selectedWorker (privileged) or current user (worker role).
 *   - workerId sent in POST body so backend stores it on the request.
 *   - Burden is NEVER applied here — backend calculates it at confirm time
 *     from WO union rates and worker.wsib.
 *
 * HST gating (mirrors backend HTML builder + app logic):
 *   - Only shown when the effective worker has collectsTax: true.
 *   - For privileged roles: selectedWorker.collectsTax.
 *   - For worker role: current user's own collectsTax from /auth/me.
 *
 * HR row:
 *   - Uses effectiveWorkerRate (worker's raw payRate, no burden division).
 *   - Backend confirm splits burden on top additively.
 */

import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { format, startOfWeek, addDays, subWeeks } from "date-fns";
import SharedPieceworkItemEntry, {
  calcLaborQtyInst,
  makeInst,
  parseInches,
  parseFt,
  validateEntries,
  LaborTotalsFooter,
} from "./SharedPieceworkItemEntry";

const API = "https://erp-production-72da01c8e651.herokuapp.com/api";

const fmt = (n) =>
  (n ?? 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
const fmtQty = (n) =>
  (n ?? 0).toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
const cls = (...c) => c.filter(Boolean).join(" ");
const TODAY = () => new Date().toISOString().slice(0, 10);

const resolveEntryFt = (val) => {
  if (val == null || val === "") return 0;
  const n = parseFt(String(val));
  return n !== null ? n : parseFloat(val) || 0;
};

// ── Pay period helpers ─────────────────────────────────────────────────────
const generatePayPeriods = (count = 10) => {
  const periods = [];
  const today = new Date();
  const thisSunday = startOfWeek(today, { weekStartsOn: 0 });
  for (let i = 0; i < count; i++) {
    const sunday = subWeeks(thisSunday, i);
    const saturday = addDays(sunday, 6);
    periods.push({
      label: `${format(sunday, "MMM d")} – ${format(saturday, "MMM d, yyyy")}`,
      date: sunday.toISOString(),
      isCurrent: i === 0,
    });
  }
  return periods;
};

// ─────────────────────────────────────────────────────────────────────────────
// WorkerPicker
// ─────────────────────────────────────────────────────────────────────────────
function WorkerPicker({ workers, selected, onSelect, loading }) {
  const [query, setQuery] = useState("");
  const filtered = useMemo(() => {
    if (!query.trim()) return workers;
    const q = query.toLowerCase();
    return workers.filter(
      (w) =>
        (w.displayName || "").toLowerCase().includes(q) ||
        (w.email || "").toLowerCase().includes(q),
    );
  }, [workers, query]);

  if (loading)
    return (
      <div className="flex items-center gap-2 py-4">
        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-orange-500" />
        <span className="text-sm text-gray-400">Loading workers…</span>
      </div>
    );

  if (!workers.length)
    return <p className="text-sm text-gray-500 py-3">No pieceworkers found.</p>;

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg focus-within:border-orange-500 transition-colors">
        <svg
          className="w-3.5 h-3.5 text-gray-500 shrink-0"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fillRule="evenodd"
            d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
            clipRule="evenodd"
          />
        </svg>
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search name or email…"
          className="flex-1 text-sm bg-transparent text-gray-200 placeholder-gray-500 focus:outline-none"
        />
        {query && (
          <button
            onClick={() => setQuery("")}
            className="text-gray-500 hover:text-gray-300 text-base leading-none"
          >
            ×
          </button>
        )}
      </div>
      <div className="max-h-56 overflow-y-auto space-y-1.5">
        {filtered.map((w) => {
          const active = selected?._id === w._id;
          return (
            <button
              key={w._id}
              onClick={() => onSelect(active ? null : w)}
              className={cls(
                "w-full flex items-center gap-3 px-4 py-3 rounded-xl border text-left transition-colors",
                active
                  ? "border-orange-500 bg-orange-900/20"
                  : "border-gray-700 bg-gray-700/40 hover:border-gray-500 hover:bg-gray-700/60",
              )}
            >
              <div
                className={cls(
                  "w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0",
                  active
                    ? "bg-orange-500/20 text-orange-400"
                    : "bg-gray-600 text-gray-400",
                )}
              >
                {(w.displayName || w.email || "?")[0].toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <p
                  className={cls(
                    "text-sm font-medium truncate",
                    active ? "text-orange-300" : "text-gray-200",
                  )}
                >
                  {w.displayName || w.email}
                </p>
                {w.email && w.displayName && (
                  <p className="text-[11px] text-gray-500 truncate">
                    {w.email}
                  </p>
                )}
              </div>
              {active && (
                <svg
                  className="w-4 h-4 text-orange-400 shrink-0"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              )}
            </button>
          );
        })}
        {filtered.length === 0 && (
          <p className="text-xs text-gray-500 text-center py-4">
            No workers match "{query}"
          </p>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// PayPeriodPicker
// ─────────────────────────────────────────────────────────────────────────────
function PayPeriodPicker({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const periods = useMemo(() => generatePayPeriods(10), []);
  const selected = value || periods[0];
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((p) => !p)}
        className={cls(
          "w-full flex items-center justify-between px-4 py-3 rounded-xl border text-left transition-colors",
          open
            ? "border-orange-500 bg-orange-900/10"
            : "border-gray-600 bg-gray-700/60 hover:border-gray-500",
        )}
      >
        <div>
          <p className="text-[10px] text-gray-500 uppercase tracking-wide mb-0.5">
            Pay Period (Week of…)
          </p>
          <p className="text-sm font-semibold text-gray-200">
            {selected.label}
          </p>
          {selected.isCurrent && (
            <p className="text-[10px] text-green-500 mt-0.5">Current week</p>
          )}
        </div>
        <svg
          className={cls(
            "w-4 h-4 text-gray-500 transition-transform shrink-0 ml-3",
            open && "rotate-180",
          )}
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fillRule="evenodd"
            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
            clipRule="evenodd"
          />
        </svg>
      </button>
      {open && (
        <div className="absolute top-full left-0 right-0 z-20 mt-1 bg-gray-800 border border-gray-600 rounded-xl shadow-2xl max-h-64 overflow-y-auto divide-y divide-gray-700/40">
          {periods.map((pp) => {
            const isSel = pp.date === selected.date;
            return (
              <button
                key={pp.date}
                type="button"
                onClick={() => {
                  onChange(pp);
                  setOpen(false);
                }}
                className={cls(
                  "w-full text-left px-4 py-3 flex items-center justify-between transition-colors",
                  isSel ? "bg-orange-900/20" : "hover:bg-gray-700/50",
                )}
              >
                <div>
                  <p
                    className={cls(
                      "text-sm font-medium",
                      isSel ? "text-orange-300" : "text-gray-200",
                    )}
                  >
                    {pp.label}
                  </p>
                  {pp.isCurrent && (
                    <p className="text-[10px] text-green-500 mt-0.5">
                      Current week
                    </p>
                  )}
                </div>
                {isSel && (
                  <svg
                    className="w-4 h-4 text-orange-400 shrink-0"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Steps indicator
// ─────────────────────────────────────────────────────────────────────────────
function Steps({ steps, current }) {
  return (
    <div className="flex items-center mb-6">
      {steps.map((s, i) => (
        <div key={s} className="flex items-center flex-1 last:flex-none">
          <div
            className={cls(
              "flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold shrink-0 border",
              i < current
                ? "bg-orange-500 border-orange-500 text-white"
                : i === current
                  ? "bg-orange-500 border-orange-400 text-white"
                  : "bg-gray-700 border-gray-600 text-gray-500",
            )}
          >
            {i < current ? "✓" : i + 1}
          </div>
          <p
            className={cls(
              "ml-1.5 text-xs font-medium hidden sm:block",
              i === current
                ? "text-orange-400"
                : i < current
                  ? "text-gray-400"
                  : "text-gray-600",
            )}
          >
            {s}
          </p>
          {i < steps.length - 1 && (
            <div
              className={cls(
                "flex-1 h-px mx-3",
                i < current ? "bg-orange-500/50" : "bg-gray-700",
              )}
            />
          )}
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// WorkOrderPicker
// ─────────────────────────────────────────────────────────────────────────────
function WorkOrderPicker({
  workOrders,
  selectedWO,
  onSelect,
  loading,
  projectName,
}) {
  return (
    <div className="bg-gray-800 rounded-xl border border-gray-700 p-5 space-y-4">
      <div>
        <h2 className="text-sm font-semibold text-white">Select Work Order</h2>
        {projectName && (
          <p className="text-xs text-orange-300 mt-0.5 font-medium">
            {projectName}
          </p>
        )}
      </div>
      {loading ? (
        <div className="flex items-center gap-2 py-4">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-orange-500" />
          <span className="text-sm text-gray-400">Loading work orders…</span>
        </div>
      ) : workOrders.length === 0 ? (
        <p className="text-sm text-gray-500 py-3">
          No piecework work orders for this project.
        </p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {workOrders.map((wo) => (
            <button
              key={wo._id}
              onClick={() => onSelect(wo)}
              className={cls(
                "text-left px-4 py-3 rounded-xl border transition-colors",
                selectedWO?._id === wo._id
                  ? "border-orange-500 bg-orange-900/20"
                  : "border-gray-700 bg-gray-700/40 hover:border-gray-500 hover:bg-gray-700/60",
              )}
            >
              <p className="text-[11px] font-mono text-orange-400 mb-0.5">
                {wo.woNumber}
              </p>
              <p className="text-sm font-medium text-gray-200">{wo.title}</p>
              {wo.bidArea && (
                <p className="text-[11px] text-gray-500 mt-0.5">{wo.bidArea}</p>
              )}
              {wo.union && wo.union !== "non-union" && (
                <span className="mt-1.5 inline-block text-[10px] bg-blue-900/40 text-blue-300 border border-blue-700/40 px-1.5 py-0.5 rounded-full">
                  {wo.union}
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SectionPicker
// ─────────────────────────────────────────────────────────────────────────────
function SectionPicker({ sections, selectedSection, onSelect, loading }) {
  return (
    <div className="bg-gray-800 rounded-xl border border-gray-700 p-5 space-y-4">
      <div>
        <h2 className="text-sm font-semibold text-white">Select Section</h2>
        <p className="text-xs text-gray-500 mt-0.5">
          Choose the type of work you performed today.
        </p>
      </div>
      {loading ? (
        <div className="flex items-center gap-2 py-4">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-orange-500" />
          <span className="text-sm text-gray-400">Loading sections…</span>
        </div>
      ) : sections.length === 0 ? (
        <p className="text-sm text-gray-500 py-3">
          No sections with templates available. Contact your administrator.
        </p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {sections.map((s) => (
            <button
              key={String(s._id)}
              onClick={() => onSelect(s)}
              className={cls(
                "text-left px-4 py-3.5 rounded-xl border transition-colors",
                selectedSection?._id === s._id || selectedSection?.id === s._id
                  ? "border-orange-500 bg-orange-900/20"
                  : "border-gray-700 bg-gray-700/40 hover:border-gray-500 hover:bg-gray-700/60",
              )}
            >
              <p className="text-sm font-semibold text-gray-200">
                {s.displayName || s.name}
              </p>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Review table
// ─────────────────────────────────────────────────────────────────────────────
function ReviewTable({
  laborItems,
  worker,
  project,
  workOrder,
  section,
  payPeriod,
  workDate,
  effectiveHstRate,
}) {
  const showHst = (effectiveHstRate || 0) > 0;
  let totalSub = 0;
  const rows = (laborItems || [])
    .filter((i) => (i.computedCoverage || i.quantity || 0) > 0)
    .map((item) => {
      const qty = item.computedCoverage || item.quantity || 0;
      const wRate = item.unitRate || 0;
      const sub = parseFloat((qty * wRate).toFixed(2));
      const hst = showHst
        ? parseFloat(((sub * effectiveHstRate) / 100).toFixed(2))
        : 0;
      totalSub += sub;
      return { ...item, qty, wRate, sub, hst, total: sub + hst };
    });
  const totalHst = showHst
    ? parseFloat(((totalSub * effectiveHstRate) / 100).toFixed(2))
    : 0;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {[
          ["Pay Period", payPeriod?.label],
          [
            "Work Date",
            workDate
              ? format(new Date(workDate + "T12:00:00"), "MMM d, yyyy")
              : "—",
          ],
          ["Worker", worker],
          ["Project", project],
          ["Work Order", workOrder],
          ["Section", section || "—"],
        ].map(([label, val]) => (
          <div
            key={label}
            className="bg-gray-900 rounded-lg border border-gray-700 p-2.5"
          >
            <p className="text-[10px] text-gray-500 uppercase tracking-wide mb-0.5">
              {label}
            </p>
            <p className="text-xs text-gray-200 font-medium truncate">
              {val || "—"}
            </p>
          </div>
        ))}
      </div>
      <div className="overflow-x-auto rounded-xl border border-gray-700">
        <table className="w-full text-xs">
          <thead className="bg-gray-900">
            <tr>
              {[
                "Labor Item",
                "Coverage / Qty",
                "Rate",
                "Subtotal",
                ...(showHst ? [`HST (${effectiveHstRate}%)`, "Total"] : []),
              ].map((h) => (
                <th
                  key={h}
                  className="px-3 py-2.5 text-left text-gray-400 font-medium whitespace-nowrap"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700/50">
            {rows.map((row, idx) => (
              <tr
                key={idx}
                className={idx % 2 === 0 ? "bg-gray-800/40" : "bg-gray-700/20"}
              >
                <td className="px-3 py-2 text-gray-200 max-w-[160px]">
                  <p className="font-medium truncate">{row.description}</p>
                  <p className="text-[10px] font-mono text-orange-400 mt-0.5">
                    {row.itemCode}
                  </p>
                </td>
                <td className="px-3 py-2 text-blue-300 font-mono font-semibold">
                  {fmtQty(row.qty)}{" "}
                  <span className="text-gray-500 font-normal">{row.uom}</span>
                </td>
                <td className="px-3 py-2 text-gray-300 font-mono">
                  ${fmt(row.wRate)}
                </td>
                <td className="px-3 py-2 text-gray-200 font-mono">
                  ${fmt(row.sub)}
                </td>
                {showHst && (
                  <td className="px-3 py-2 text-gray-400 font-mono">
                    ${fmt(row.hst)}
                  </td>
                )}
                {showHst && (
                  <td className="px-3 py-2 text-orange-300 font-mono font-semibold">
                    ${fmt(row.total)}
                  </td>
                )}
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td
                  colSpan={showHst ? 6 : 4}
                  className="px-3 py-6 text-center text-gray-500 text-sm"
                >
                  No labor items with quantities entered.
                </td>
              </tr>
            )}
          </tbody>
          <tfoot className="bg-gray-900 border-t-2 border-orange-400/30">
            <tr>
              <td
                colSpan={3}
                className="px-3 py-2.5 text-xs text-gray-400 font-semibold"
              >
                Labor Totals
              </td>
              <td className="px-3 py-2.5 font-bold text-gray-200 font-mono">
                ${fmt(totalSub)}
              </td>
              {showHst && (
                <td className="px-3 py-2.5 font-bold text-gray-300 font-mono">
                  ${fmt(totalHst)}
                </td>
              )}
              {showHst && (
                <td className="px-3 py-2.5 font-bold text-orange-300 font-mono text-sm">
                  ${fmt(totalSub + totalHst)}
                </td>
              )}
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Totals summary */}
      <div className="bg-gray-900 rounded-xl border border-gray-700 p-4">
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <div className="flex items-center justify-between gap-8">
              <span className="text-xs text-gray-500">Subtotal</span>
              <span className="text-xs font-mono text-gray-300">
                ${fmt(totalSub)}
              </span>
            </div>
            {showHst && (
              <div className="flex items-center justify-between gap-8">
                <span className="text-xs text-gray-500">
                  HST ({effectiveHstRate}%)
                </span>
                <span className="text-xs font-mono text-gray-300">
                  ${fmt(totalHst)}
                </span>
              </div>
            )}
          </div>
          <div className="text-right ml-8">
            <p className="text-[10px] text-gray-500 uppercase tracking-wide mb-0.5">
              Total
            </p>
            <p className="text-xl font-bold text-orange-300 font-mono">
              ${fmt(totalSub + totalHst)}
            </p>
          </div>
        </div>
        {showHst && (
          <p className="text-[10px] text-gray-600 mt-2">
            Burden applied by payroll at confirmation — not included here.
          </p>
        )}
        {!showHst && (
          <p className="text-[10px] text-gray-600 mt-2">
            No HST — burden applied by payroll at confirmation.
          </p>
        )}
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═════════════════════════════════════════════════════════════════════════════
export default function CreatePieceworkRequest() {
  const navigate = useNavigate();
  const location = useLocation();
  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  const navState = location.state || {};
  const contextProjectId = navState.projectId || null;
  const contextProjectName = navState.projectName || null;
  const contextWorkOrderId = navState.workOrderId || null;
  const contextWorkOrderName = navState.workOrderName || null;
  const contextWorkOrders = navState.workOrders || null;

  const hasProjectContext = !!contextProjectId;
  const hasFullContext = !!(contextProjectId && contextWorkOrderId);

  const STEPS = hasFullContext
    ? ["Section", "Pay Period", "Work Items", "Review & Submit"]
    : hasProjectContext
      ? ["Work Order", "Section", "Pay Period", "Work Items", "Review & Submit"]
      : [
          "Project & WO",
          "Section",
          "Pay Period",
          "Work Items",
          "Review & Submit",
        ];

  const STEP = hasFullContext
    ? { section: 0, payPeriod: 1, items: 2, review: 3 }
    : hasProjectContext
      ? { workOrder: 0, section: 1, payPeriod: 2, items: 3, review: 4 }
      : { projectWO: 0, section: 1, payPeriod: 2, items: 3, review: 4 };

  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(null);

  // ── Current user ──────────────────────────────────────────────────────────
  const [currentUserId, setCurrentUserId] = useState(null);
  const [currentUserName, setCurrentUserName] = useState("You");
  const [currentUserRole, setCurrentUserRole] = useState("");
  const [currentUserPayRate, setCurrentUserPayRate] = useState(0);
  const [currentUserCollectsTax, setCurrentUserCollectsTax] = useState(false);

  // ── Worker picker (privileged roles only) ─────────────────────────────────
  const [pieceworkers, setPieceworkers] = useState([]);
  const [loadingWorkers, setLoadingWorkers] = useState(false);
  const [selectedWorker, setSelectedWorker] = useState(null);

  const isPrivileged = ["admin", "project_manager", "foreman"].includes(
    currentUserRole,
  );

  // Effective values — derive from selectedWorker (privileged) or current user
  const effectiveWorkerId = isPrivileged
    ? selectedWorker?._id || null
    : currentUserId;
  const effectiveWorkerName = isPrivileged
    ? selectedWorker?.displayName || selectedWorker?.email || "—"
    : currentUserName;
  // Raw payRate — no burden division. Backend applies burden additively at confirm.
  const effectiveWorkerRate = isPrivileged
    ? parseFloat(selectedWorker?.payRate) || 0
    : currentUserPayRate;

  // HST only applied when the effective worker has collectsTax: true
  const effectiveHstRate = useMemo(() => {
    if (isPrivileged) {
      if (!selectedWorker) return 0;
      return selectedWorker.collectsTax === true
        ? parseFloat(selectedWorker.taxRate || hstRate) || 0
        : 0;
    }
    return currentUserCollectsTax ? hstRate : 0;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isPrivileged,
    selectedWorker,
    currentUserCollectsTax /* hstRate below */,
  ]);

  // ── Project / WO ──────────────────────────────────────────────────────────
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [workOrders, setWorkOrders] = useState([]);
  const [selectedWO, setSelectedWO] = useState(null);
  const [loadingWOs, setLoadingWOs] = useState(false);

  // ── Sections ──────────────────────────────────────────────────────────────
  const [sections, setSections] = useState([]);
  const [selectedSection, setSelectedSection] = useState(null);
  const [loadingSections, setLoadingSections] = useState(false);

  // ── Pay period / dates ────────────────────────────────────────────────────
  const payPeriods = useMemo(() => generatePayPeriods(10), []);
  const [payPeriod, setPayPeriod] = useState(payPeriods[0]);
  const [workDate, setWorkDate] = useState(TODAY());

  // ── HST ───────────────────────────────────────────────────────────────────
  const [hstRate, setHstRate] = useState(13);

  // ── Notes / hours ─────────────────────────────────────────────────────────
  const [notes, setNotes] = useState("");
  const [hoursWorked, setHoursWorked] = useState("");

  // ── Modules / instances ───────────────────────────────────────────────────
  const [modules, setModules] = useState([]);
  const [selectedModule, setSelectedModule] = useState(null);
  const [loadingModules, setLoadingModules] = useState(false);
  const [instances, setInstances] = useState([]);
  const [sizeErrors, setSizeErrors] = useState(new Set());

  // ── Init: /auth/me ────────────────────────────────────────────────────────
  useEffect(() => {
    axios
      .get(`${API}/auth/me`, { headers })
      .then((r) => {
        const u = r.data?.user;
        setCurrentUserId(u?._id || u?.id || null);
        setCurrentUserName(u?.displayName || u?.email || "You");
        setCurrentUserPayRate(parseFloat(u?.payRate) || 0);
        setCurrentUserRole(u?.role || u?.userRole || "");
        setCurrentUserCollectsTax(u?.collectsTax || false);
      })
      .catch(() => {});
  }, []);

  // ── Fetch pieceworkers (privileged only) ──────────────────────────────────
  useEffect(() => {
    if (!currentUserRole || !isPrivileged) return;
    setLoadingWorkers(true);
    axios
      .get(`${API}/auth/users`, {
        headers,
        params: { role: "worker", limit: 200 },
      })
      .then((r) => {
        const raw =
          r.data.users || r.data.data || (Array.isArray(r.data) ? r.data : []);
        setPieceworkers(
          raw.filter((u) => ["worker", "pieceworker"].includes(u.role)),
        );
      })
      .catch(() => {
        // fallback to mobile endpoint
        axios
          .get(`${API}/mobile/users/pieceworkers`, { headers })
          .then((r) => setPieceworkers(Array.isArray(r.data) ? r.data : []))
          .catch(() => setPieceworkers([]));
      })
      .finally(() => setLoadingWorkers(false));
  }, [currentUserRole, isPrivileged]);

  // ── Company profile (HST rate) ────────────────────────────────────────────
  useEffect(() => {
    axios
      .get(`${API}/company-profile`, { headers })
      .then((r) => setHstRate(parseFloat(r.data?.defaultTax?.taxRate || 13)))
      .catch(() => {});
  }, []);

  // ── Context pre-fill ──────────────────────────────────────────────────────
  useEffect(() => {
    if (hasProjectContext)
      setSelectedProject({
        _id: contextProjectId,
        projectName: contextProjectName,
      });
    if (hasFullContext)
      setSelectedWO({
        _id: contextWorkOrderId,
        title: contextWorkOrderName,
        woNumber: contextWorkOrderName?.split(" – ")[0] || "",
      });
  }, []); // eslint-disable-line

  // ── Projects (Tier 1 only) ────────────────────────────────────────────────
  useEffect(() => {
    if (hasProjectContext) return;
    axios
      .get(`${API}/projects`, { headers })
      .then((r) => setProjects(r.data?.projects || r.data || []))
      .catch(() => {});
  }, [hasProjectContext]);

  // ── Work orders ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (hasFullContext) return;
    if (!selectedProject?._id) return;
    if (hasProjectContext && contextWorkOrders?.length) {
      setWorkOrders(
        contextWorkOrders.filter((w) =>
          ["piecework", "union_piecework"].includes(w.type),
        ),
      );
      return;
    }
    setLoadingWOs(true);
    setSelectedWO(null);
    setWorkOrders([]);
    axios
      .get(`${API}/workorders3/project/${selectedProject._id}`, { headers })
      .then((r) =>
        setWorkOrders(
          (r.data.workOrders || []).filter((w) =>
            ["piecework", "union_piecework"].includes(w.type),
          ),
        ),
      )
      .catch(() => setWorkOrders([]))
      .finally(() => setLoadingWOs(false));
  }, [selectedProject?._id, hasFullContext, hasProjectContext]);

  // ── Load sections ─────────────────────────────────────────────────────────
  const loadSections = useCallback(async () => {
    if (!selectedWO?._id) return;
    setLoadingSections(true);
    setSections([]);
    setSelectedSection(null);
    try {
      const [ccRes, modRes] = await Promise.all([
        axios.get(`${API}/company-profile/cost-codes`, { headers }),
        axios.get(`${API}/pieceworkRequests/modules`, {
          headers,
          params: {
            workOrderId: selectedWO._id,
            projectId: selectedProject?._id,
          },
        }),
      ]);
      const activeCodes = (ccRes.data || []).filter((c) => c.active !== false);
      const mods = modRes.data.modules || [];

      const displayNameByCostCode = new Map();
      for (const m of mods) {
        const cid = String(m.costCode?.id);
        if (cid && !displayNameByCostCode.has(cid)) {
          displayNameByCostCode.set(
            cid,
            m.sectionDisplayName || m.costCode?.name || "",
          );
        }
      }
      const idsWithModules = new Set(displayNameByCostCode.keys());
      const filtered = activeCodes
        .filter((c) => idsWithModules.has(String(c._id)))
        .map((c) => ({
          ...c,
          displayName: displayNameByCostCode.get(String(c._id)) || c.name || "",
        }));
      setSections(filtered);
      if (filtered.length === 1) setSelectedSection(filtered[0]);
    } catch {
      setSections([]);
    } finally {
      setLoadingSections(false);
    }
  }, [selectedWO?._id, selectedProject?._id]);

  // For hasFullContext, auto-load sections on mount
  useEffect(() => {
    if (hasFullContext && selectedWO?._id) loadSections();
  }, [hasFullContext, selectedWO?._id]); // eslint-disable-line

  // ── Load modules ──────────────────────────────────────────────────────────
  const loadModules = useCallback(
    async (costCodeId) => {
      if (!selectedWO?._id || !costCodeId) return;
      setLoadingModules(true);
      setModules([]);
      setSelectedModule(null);
      setInstances([]);
      setSizeErrors(new Set());
      try {
        const res = await axios.get(`${API}/pieceworkRequests/modules`, {
          headers,
          params: {
            workOrderId: selectedWO._id,
            projectId: selectedProject?._id,
            costCodeId,
          },
        });
        const mods = res.data.modules || [];
        setModules(mods);
        if (mods.length === 1) applyModule(mods[0]);
      } catch {
        setModules([]);
      } finally {
        setLoadingModules(false);
      }
    },
    [selectedWO?._id, selectedProject?._id],
  );

  const applyModule = (mod) => {
    setSelectedModule(mod);
    const init = [];
    (mod.pairedItems || []).forEach((paired, pIdx) => {
      const lab = paired.labor || {};
      const defH = lab.defaultHeightFt || 0;
      const defL = lab.defaultLengthFt || 0;
      const defIn = lab.defaultLengthInches || 0;

      const lfDefault =
        lab.defaultLengthInput ||
        (defIn > 0
          ? String(defIn)
          : defL > 0
            ? String(parseFloat((defL * 12).toFixed(4)))
            : "");

      const defaultQtyVal =
        lab.defaultQty != null && lab.defaultQty !== ""
          ? String(lab.defaultQty)
          : (paired.suggestedQty || 0) > 0
            ? String(paired.suggestedQty)
            : "";

      init.push(
        makeInst(pIdx, {
          qty: defaultQtyVal,
          lengthIn: lfDefault,
          height: defH > 0 ? String(defH) : "",
          length: defL > 0 ? String(defL) : "",
          combinedDim: defH > 0 && defL > 0 ? `${defH}x${defL}` : "",
        }),
      );
    });
    setInstances(init);
    setSizeErrors(new Set());
  };

  const handleSectionSelect = (section) => {
    setSelectedSection(section);
    loadModules(String(section._id || section.id));
  };

  // ── Instance handlers ─────────────────────────────────────────────────────
  const handleInstChange = useCallback((instId, field, value) => {
    setInstances((prev) =>
      prev.map((inst) =>
        inst._instId === instId ? { ...inst, [field]: value } : inst,
      ),
    );
    setSizeErrors(new Set());
  }, []);

  const handleDuplicate = useCallback((instId) => {
    setInstances((prev) => {
      const idx = prev.findIndex((i) => i._instId === instId);
      if (idx === -1) return prev;
      const orig = prev[idx];
      const copy = makeInst(orig.pairedIdx, {
        height: orig.height,
        length: orig.length,
        combinedDim: orig.combinedDim,
        lengthIn: orig.lengthIn,
        qty: "",
      });
      const next = [...prev];
      next.splice(idx + 1, 0, copy);
      return next;
    });
    setSizeErrors(new Set());
  }, []);

  const handleRemove = useCallback((instId) => {
    setInstances((prev) => prev.filter((i) => i._instId !== instId));
    setSizeErrors(new Set());
  }, []);

  // ── Active instance count ─────────────────────────────────────────────────
  const activeInstCount = useMemo(() => {
    if (!selectedModule) return 0;
    return instances.filter((inst) => {
      const paired = selectedModule.pairedItems?.[inst.pairedIdx];
      if (!paired) return false;
      return calcLaborQtyInst(paired, inst) > 0;
    }).length;
  }, [selectedModule, instances]);

  // ── canProceed ────────────────────────────────────────────────────────────
  const canProceed = useMemo(() => {
    if (step === STEP.projectWO) return !!(selectedProject && selectedWO);
    if (step === STEP.workOrder)
      return !!selectedWO && (!isPrivileged || !!selectedWorker);
    if (step === STEP.section) return !!selectedSection;
    if (step === STEP.payPeriod) return !!payPeriod;
    if (step === STEP.items)
      return (
        activeInstCount > 0 ||
        (hoursWorked !== "" && parseFloat(hoursWorked) > 0)
      );
    if (step === STEP.review) return notes.trim().length > 0;
    return true;
  }, [
    step,
    selectedProject,
    selectedWO,
    selectedWorker,
    isPrivileged,
    selectedSection,
    payPeriod,
    activeInstCount,
    hoursWorked,
    notes,
    STEP,
  ]);

  // ── Size key helper (mirrors SharedPieceworkItemEntry) ────────────────────
  const sizeKeyLocal = (inst, laborUom) => {
    const uom = (laborUom || "").toUpperCase();
    if (uom.includes("SF")) {
      const cd = (inst.combinedDim || "").trim();
      if (cd) return cd.toLowerCase().replace(/\s+/g, "");
      return `${resolveEntryFt(inst.height).toFixed(4)}x${resolveEntryFt(inst.length).toFixed(4)}`;
    }
    if (uom.includes("LF")) {
      const inches = parseInches(String(inst.lengthIn ?? ""));
      return inches !== null
        ? inches.toFixed(4)
        : String(inst.lengthIn ?? "").trim();
    }
    return "";
  };

  // ── Build line items ──────────────────────────────────────────────────────
  const buildLineItems = () => {
    if (!selectedModule) return [];
    const lineItems = [];
    const byPaired = new Map();
    for (const inst of instances) {
      if (!byPaired.has(inst.pairedIdx)) byPaired.set(inst.pairedIdx, []);
      byPaired.get(inst.pairedIdx).push(inst);
    }
    for (const [pIdx, insts] of byPaired.entries()) {
      const paired = selectedModule.pairedItems?.[pIdx];
      if (!paired) continue;
      for (const inst of insts) {
        const lQty = calcLaborQtyInst(paired, inst);
        if (lQty <= 0) continue;
        const qty = parseFloat(inst.qty) || 0;
        const heightFt = resolveEntryFt(inst.height);
        const lengthFt = resolveEntryFt(inst.length);
        const lengthInVal = parseInches(String(inst.lengthIn ?? "")) ?? 0;
        const lfLengthFt = lengthInVal / 12;
        const isLaborLF = (paired.labor.uom || "").toUpperCase().includes("LF");
        const isMatLF =
          paired.material &&
          (paired.material.uom || "").toUpperCase().includes("LF");

        if (paired.material && qty > 0) {
          lineItems.push({
            type: "material",
            description: paired.material.name || "",
            uom: paired.material.uom || "",
            quantity: qty,
            height: heightFt,
            length: isMatLF ? lfLengthFt : lengthFt,
            sectionNumber: "",
            poItemCode: "",
          });
        }

        lineItems.push({
          budgetItemId: null,
          itemCode: paired.labor.itemCode,
          description: paired.labor.description,
          sectionNumber: selectedSection?.code || "",
          sectionName: selectedSection?.name || "",
          bidArea: selectedWO?.bidArea || "",
          areaType: selectedSection?.name || "",
          uom: paired.labor.uom,
          height: heightFt,
          length: isLaborLF ? lfLengthFt : lengthFt,
          computedCoverage: lQty,
          quantity: lQty,
          unitRate: paired.labor.unitRate || 0,
          rateSource: paired.labor.rateSource || "db",
          moduleItemId: `${selectedModule._id}_${pIdx}`,
          notes: "",
        });
      }
    }

    // HR row — raw payRate, no burden. Backend adds burden at confirm.
    const hrs = parseFloat(hoursWorked) || 0;
    if (hrs > 0 && effectiveWorkerRate > 0) {
      lineItems.push({
        budgetItemId: null,
        itemCode: selectedSection?.code || "LABOUR-HR",
        description: `Labour Hours — ${selectedSection?.name || ""}`,
        sectionNumber: selectedSection?.code || "",
        sectionName: selectedSection?.name || "",
        bidArea: selectedWO?.bidArea || "",
        areaType: selectedSection?.name || "",
        uom: "HR",
        height: 0,
        length: 0,
        computedCoverage: 0,
        quantity: hrs,
        unitRate: effectiveWorkerRate,
        rateSource: "manual",
        moduleItemId: "",
        notes: "Labour hours",
      });
    }
    return lineItems;
  };

  // ── Review labor items ────────────────────────────────────────────────────
  const reviewLaborItems = useMemo(() => {
    if (!selectedModule) return [];
    const items = [];
    const byPaired = new Map();
    for (const inst of instances) {
      if (!byPaired.has(inst.pairedIdx)) byPaired.set(inst.pairedIdx, []);
      byPaired.get(inst.pairedIdx).push(inst);
    }
    for (const [pIdx, insts] of byPaired.entries()) {
      const paired = selectedModule.pairedItems?.[pIdx];
      if (!paired) continue;
      for (const inst of insts) {
        const lQty = calcLaborQtyInst(paired, inst);
        if (lQty <= 0) continue;
        items.push({
          itemCode: paired.labor?.itemCode || "",
          description: paired.labor?.description || "",
          uom: paired.labor?.uom || "",
          unitRate: paired.labor?.unitRate || 0,
          computedCoverage: lQty,
          quantity: lQty,
        });
      }
    }
    const reviewHrs = parseFloat(hoursWorked) || 0;
    if (reviewHrs > 0 && effectiveWorkerRate > 0) {
      items.push({
        itemCode: selectedSection?.code || "LABOUR-HR",
        description: `Labour Hours — ${selectedSection?.name || ""}`,
        uom: "HR",
        unitRate: effectiveWorkerRate,
        computedCoverage: reviewHrs,
        quantity: reviewHrs,
      });
    }
    return items;
  }, [
    selectedModule,
    instances,
    hoursWorked,
    effectiveWorkerRate,
    selectedSection,
  ]);

  // ── handleNext ────────────────────────────────────────────────────────────
  const handleNext = () => {
    setError("");
    if (step === STEP.workOrder || (hasFullContext && step === 0))
      loadSections();
    if (step === STEP.section && selectedSection)
      loadModules(String(selectedSection._id || selectedSection.id));

    if (step === STEP.items && selectedModule) {
      const errs = validateEntries(instances, selectedModule);
      if (errs.length > 0) {
        const errSet = new Set();
        const byPaired = new Map();
        for (const inst of instances) {
          if (!byPaired.has(inst.pairedIdx)) byPaired.set(inst.pairedIdx, []);
          byPaired.get(inst.pairedIdx).push(inst);
        }
        for (const [pIdx, insts] of byPaired.entries()) {
          if (insts.length < 2) continue;
          const paired = selectedModule.pairedItems?.[pIdx];
          if (!paired) continue;
          const uom = paired.labor?.uom || "";
          const seen = new Map();
          for (const inst of insts) {
            const k = sizeKeyLocal(inst, uom);
            if (k && seen.has(k)) {
              errSet.add(inst._instId);
              errSet.add(seen.get(k));
            } else if (k) seen.set(k, inst._instId);
          }
        }
        setSizeErrors(errSet);
        setError(errs.join(" "));
        return;
      }
    }
    setStep((s) => s + 1);
  };

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (isPrivileged && !effectiveWorkerId) {
      setError("Please select a worker before submitting.");
      return;
    }
    if (selectedModule) {
      const errs = validateEntries(instances, selectedModule);
      if (errs.length > 0) {
        setError(errs.join(" "));
        return;
      }
    }
    setSubmitting(true);
    setError("");
    try {
      const lineItems = buildLineItems();
      const laborOnly = lineItems.filter((li) => li.type !== "material");
      if (!laborOnly.length) {
        setError("Please enter quantities for at least one item.");
        setSubmitting(false);
        return;
      }
      const res = await axios.post(
        `${API}/pieceworkRequests`,
        {
          projectId: selectedProject._id,
          workOrderId: selectedWO._id,
          workerId: effectiveWorkerId, // backend: resolvedWorkerId = workerId || req.user.id
          workCompletedDate: workDate,
          areaType: selectedSection?.name || "",
          lineItems,
          moduleId: selectedModule?._id || null,
          moduleName: selectedModule?.name || "",
          notes,
        },
        { headers },
      );
      setSuccess(res.data.data);
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  // ── Navigation helpers ────────────────────────────────────────────────────
  const goBackToProject = () => {
    if (contextProjectId) {
      navigate(`/Projects/${contextProjectId}?tab=Site+Requests`, {
        state: { returnTab: "Site Requests", siteRequestsView: "requests" },
      });
    } else {
      navigate(-1);
    }
  };

  const resetAll = () => {
    setStep(0);
    if (!hasProjectContext) setSelectedProject(null);
    if (!hasFullContext) setSelectedWO(null);
    if (isPrivileged) setSelectedWorker(null);
    setSelectedSection(null);
    setSelectedModule(null);
    setInstances([]);
    setSizeErrors(new Set());
    setNotes("");
    setHoursWorked("");
    setWorkDate(TODAY());
  };

  // ── Success screen ────────────────────────────────────────────────────────
  if (success) {
    return (
      <div className="max-w-lg mx-auto px-4 py-12 text-center space-y-5">
        <div className="w-16 h-16 rounded-full bg-green-900/40 border border-green-700/50 flex items-center justify-center mx-auto">
          <svg
            className="w-8 h-8 text-green-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 13l4 4L19 7"
            />
          </svg>
        </div>
        <h2 className="text-xl font-bold text-white">Submitted Successfully</h2>
        <p className="text-sm text-gray-400">
          Request{" "}
          <span className="text-orange-400 font-mono">
            {success.requestNumber}
          </span>{" "}
          submitted. Your foreman will review and confirm it.
        </p>
        <div className="bg-gray-800 rounded-xl border border-gray-700 p-4 text-left space-y-3">
          {[
            ["Request #", success.requestNumber, "font-mono text-orange-400"],
            [
              "Status",
              "Pending foreman review",
              "text-yellow-400 font-semibold",
            ],
            [
              "Labor Total (incl. HST)",
              `$${fmt(success.totalCost)}`,
              "text-orange-300 font-mono font-bold text-base",
            ],
          ].map(([label, val, valCls]) => (
            <div key={label} className="flex justify-between items-center">
              <span className="text-xs text-gray-500">{label}</span>
              <span className={cls("text-xs", valCls)}>{val}</span>
            </div>
          ))}
        </div>
        <div className="flex gap-3 justify-center pt-2">
          <button
            onClick={() => {
              setSuccess(null);
              resetAll();
            }}
            className="px-5 py-2 text-sm font-semibold text-white bg-orange-500 rounded-lg hover:bg-orange-600 transition-colors"
          >
            Submit Another
          </button>
          <button
            onClick={goBackToProject}
            className="px-5 py-2 text-sm text-gray-300 bg-gray-700 border border-gray-600 rounded-lg hover:bg-gray-600 transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // ── Worker picker panel (reused in WO step for both Tier 1 and Tier 2) ───
  const WorkerPickerPanel = () =>
    isPrivileged && selectedWO ? (
      <div className="bg-gray-800 rounded-xl border border-gray-700 p-5 space-y-4 mt-3">
        <div>
          <h2 className="text-sm font-semibold text-white">Select Worker</h2>
          <p className="text-xs text-gray-500 mt-0.5">
            Choose the pieceworker this log will be attributed to.
          </p>
        </div>
        {selectedWorker ? (
          <div className="flex items-center gap-3 px-4 py-3 bg-orange-900/20 border border-orange-700/40 rounded-xl">
            <div className="w-8 h-8 rounded-full bg-orange-500/20 flex items-center justify-center text-xs font-bold text-orange-400 shrink-0">
              {(selectedWorker.displayName ||
                selectedWorker.email ||
                "?")[0].toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-orange-300 truncate">
                {selectedWorker.displayName || selectedWorker.email}
              </p>
              {selectedWorker.email && selectedWorker.displayName && (
                <p className="text-[11px] text-gray-500 truncate">
                  {selectedWorker.email}
                </p>
              )}
            </div>
            <button
              onClick={() => setSelectedWorker(null)}
              className="text-xs text-gray-500 hover:text-gray-300 bg-gray-700 border border-gray-600 px-2.5 py-1 rounded-lg transition-colors shrink-0"
            >
              Change
            </button>
          </div>
        ) : (
          <WorkerPicker
            workers={pieceworkers}
            selected={selectedWorker}
            onSelect={setSelectedWorker}
            loading={loadingWorkers}
          />
        )}
        {!selectedWorker && (
          <p className="text-[11px] text-amber-500">
            A worker must be selected before proceeding.
          </p>
        )}
      </div>
    ) : null;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="mx-auto px-4 py-6 space-y-5">
      {/* Header */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 px-5 py-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h1 className="text-lg font-bold text-white">
              Submit Daily Work Log
            </h1>
            {hasProjectContext && (
              <div className="flex flex-wrap gap-2 mt-1.5">
                <span className="text-[11px] bg-gray-700 text-gray-300 border border-gray-600 px-2 py-0.5 rounded-full">
                  {contextProjectName}
                </span>
                {hasFullContext && contextWorkOrderName && (
                  <span className="text-[11px] bg-orange-900/40 text-orange-300 border border-orange-700/40 px-2 py-0.5 rounded-full font-mono">
                    {contextWorkOrderName}
                  </span>
                )}
              </div>
            )}
          </div>
          {/* Show selected worker badge for privileged roles */}
          {isPrivileged && selectedWorker && (
            <div className="bg-green-950/30 border border-green-800/40 rounded-lg px-3 py-2 text-[11px] text-green-300 shrink-0">
              On behalf of: {selectedWorker.displayName || selectedWorker.email}
            </div>
          )}
        </div>
      </div>

      <Steps steps={STEPS} current={step} />

      {error && (
        <div className="px-4 py-3 bg-red-900/40 border border-red-700/50 rounded-xl text-sm text-red-300">
          {error}
        </div>
      )}

      {/* ── Step: Project & WO (Tier 1) ──────────────────────────────────── */}
      {!hasProjectContext && step === STEP.projectWO && (
        <div className="bg-gray-800 rounded-xl border border-gray-700 p-5 space-y-5">
          <h2 className="text-sm font-semibold text-white">
            Select Project & Work Order
          </h2>
          <div>
            <label className="text-xs text-gray-400 uppercase tracking-wide mb-2 block">
              Project
            </label>
            <div className="space-y-2 max-h-60 overflow-y-auto pr-1">
              {projects.map((p) => (
                <button
                  key={p._id}
                  onClick={() => setSelectedProject(p)}
                  className={cls(
                    "w-full text-left px-4 py-3 rounded-xl border transition-colors",
                    selectedProject?._id === p._id
                      ? "border-orange-500 bg-orange-900/20"
                      : "border-gray-700 bg-gray-700/40 hover:border-gray-500 hover:bg-gray-700/60",
                  )}
                >
                  <p className="text-sm font-medium text-gray-200">
                    {p.projectName}
                  </p>
                  <p className="text-[11px] text-gray-500 mt-0.5">
                    #{p.projectNumber}
                  </p>
                </button>
              ))}
              {projects.length === 0 && (
                <p className="text-sm text-gray-500 text-center py-6">
                  No projects found
                </p>
              )}
            </div>
          </div>
          {selectedProject && (
            <WorkOrderPicker
              workOrders={workOrders}
              selectedWO={selectedWO}
              onSelect={(wo) => {
                setSelectedWO(wo);
                setSelectedWorker(null);
              }}
              loading={loadingWOs}
              projectName={null}
            />
          )}
          <WorkerPickerPanel />
        </div>
      )}

      {/* ── Step: WO (Tier 2) ─────────────────────────────────────────────── */}
      {hasProjectContext && !hasFullContext && step === STEP.workOrder && (
        <div className="space-y-0">
          <WorkOrderPicker
            workOrders={workOrders}
            selectedWO={selectedWO}
            onSelect={(wo) => {
              setSelectedWO(wo);
              setSelectedWorker(null);
            }}
            loading={loadingWOs}
            projectName={contextProjectName}
          />
          <WorkerPickerPanel />
        </div>
      )}

      {/* ── Step: Section ─────────────────────────────────────────────────── */}
      {step === STEP.section && (
        <SectionPicker
          sections={sections}
          selectedSection={selectedSection}
          onSelect={handleSectionSelect}
          loading={loadingSections}
        />
      )}

      {/* ── Step: Pay Period ──────────────────────────────────────────────── */}
      {step === STEP.payPeriod && (
        <div className="bg-gray-800 rounded-xl border border-gray-700 p-5 space-y-4">
          <h2 className="text-sm font-semibold text-white">Pay Period</h2>
          <p className="text-xs text-gray-400">
            Select the billing week this work falls under.
          </p>
          <PayPeriodPicker value={payPeriod} onChange={setPayPeriod} />

          {/* Work date inside pay period step */}
          <div>
            <label className="text-xs text-gray-400 uppercase tracking-wide mb-1.5 block">
              Work Completed Date
            </label>
            <input
              type="date"
              value={workDate}
              max={TODAY()}
              onChange={(e) => setWorkDate(e.target.value)}
              className="w-full px-3 py-2 text-sm bg-gray-700 border border-gray-600 rounded-lg text-gray-200 focus:border-orange-500 focus:outline-none"
            />
          </div>

          <div className="bg-gray-900/60 rounded-xl border border-gray-700 p-4 space-y-2">
            <p className="text-[10px] text-gray-500 uppercase tracking-wide mb-2">
              Work Context
            </p>
            {isPrivileged && selectedWorker && (
              <div className="flex justify-between">
                <span className="text-xs text-gray-500">Worker</span>
                <span className="text-xs text-green-300 font-medium">
                  {selectedWorker.displayName || selectedWorker.email}
                </span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-xs text-gray-500">Project</span>
              <span className="text-xs text-gray-200 font-medium">
                {selectedProject?.projectName || contextProjectName || "—"}
              </span>
            </div>
            {selectedWO && (
              <>
                <div className="h-px bg-gray-700" />
                <div className="flex justify-between">
                  <span className="text-xs text-gray-500">Work Order</span>
                  <span className="text-xs text-orange-400 font-mono">
                    {selectedWO.woNumber ? `${selectedWO.woNumber} — ` : ""}
                    {selectedWO.title}
                  </span>
                </div>
              </>
            )}
            {selectedSection && (
              <>
                <div className="h-px bg-gray-700" />
                <div className="flex justify-between">
                  <span className="text-xs text-gray-500">Section</span>
                  <span className="text-xs text-gray-200 font-medium">
                    {selectedSection?.displayName ||
                      selectedSection?.name ||
                      ""}
                  </span>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── Step: Work Items ──────────────────────────────────────────────── */}
      {step === STEP.items && (
        <div className="space-y-4">
          {loadingModules ? (
            <div className="bg-gray-800 rounded-xl border border-gray-700 p-8 text-center">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500 mx-auto mb-2" />
              <p className="text-sm text-gray-400">Loading work templates…</p>
            </div>
          ) : modules.length > 1 ? (
            <div className="bg-gray-800 rounded-xl border border-gray-700 p-4 space-y-3">
              <h3 className="text-sm font-semibold text-white">
                Work Templates
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {modules.map((mod) => (
                  <button
                    key={mod._id}
                    onClick={() => applyModule(mod)}
                    className={cls(
                      "text-left px-4 py-3 rounded-xl border transition-colors",
                      selectedModule?._id === mod._id
                        ? "border-orange-500 bg-orange-900/20"
                        : "border-gray-700 bg-gray-700/40 hover:border-gray-500",
                    )}
                  >
                    <p className="text-sm font-medium text-gray-200">
                      {mod.name}
                    </p>
                    <p className="text-[11px] text-gray-500 mt-0.5">
                      {(mod.pairedItems || []).length} item
                      {(mod.pairedItems || []).length !== 1 ? "s" : ""}
                    </p>
                  </button>
                ))}
              </div>
            </div>
          ) : modules.length === 0 ? (
            <div className="bg-gray-800/60 rounded-xl border border-gray-700/60 px-4 py-6 text-center">
              <p className="text-sm text-gray-500">
                No templates for{" "}
                <span className="text-orange-400">{selectedSection?.name}</span>
                . Contact your administrator.
              </p>
            </div>
          ) : null}

          {selectedModule && (
            <SharedPieceworkItemEntry
              module={selectedModule}
              instances={instances}
              onInstChange={handleInstChange}
              onDuplicate={handleDuplicate}
              onRemove={handleRemove}
              sizeErrors={sizeErrors}
              workDate={workDate}
              onWorkDateChange={setWorkDate}
              hstRate={effectiveHstRate}
              workerBurdenMultiplier={1} // no burden preview — applied at confirm
              totalBurdenPct={0}
            />
          )}

          {/* Labour Hours */}
          {selectedModule && (
            <div className="bg-gray-800 rounded-xl border border-gray-700 p-4 space-y-3">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                    Labour Hours
                    <span className="text-[9px] font-normal text-gray-500 bg-gray-700 px-1.5 py-0.5 rounded border border-gray-600">
                      {selectedSection?.name || ""}
                    </span>
                  </h3>
                  <p className="text-[11px] text-gray-500 mt-0.5">
                    How many hours did you work today on this work type?
                  </p>
                </div>
                {effectiveWorkerRate > 0 && (
                  <div className="text-right shrink-0">
                    <p className="text-[10px] text-gray-500">Hourly rate:</p>
                    <p className="text-sm font-mono font-bold text-green-400">
                      ${fmt(effectiveWorkerRate)}/HR
                    </p>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3 items-end">
                <div>
                  <label className="text-[9px] text-gray-500 uppercase tracking-wide block mb-1">
                    Hours Worked
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.25"
                    value={hoursWorked}
                    onChange={(e) => setHoursWorked(e.target.value)}
                    placeholder="e.g. 8  or  7.5"
                    className="w-full px-2 py-1.5 text-sm bg-gray-700 border border-gray-600 rounded-lg font-mono text-gray-200 placeholder-gray-600 focus:border-orange-500 focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  />
                </div>
                {parseFloat(hoursWorked) > 0 && effectiveWorkerRate > 0 && (
                  <div className="px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg">
                    <p className="text-[9px] text-gray-500 uppercase tracking-wide mb-0.5">
                      Est. hourly earnings
                    </p>
                    <p className="text-sm font-mono font-bold text-green-400">
                      ${fmt(parseFloat(hoursWorked) * effectiveWorkerRate)}
                    </p>
                  </div>
                )}
              </div>
              {hoursWorked === "" && activeInstCount === 0 && (
                <p className="text-[10px] text-amber-600">
                  Enter item quantities above, or log your hours here to
                  continue.
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Step: Review & Submit ─────────────────────────────────────────── */}
      {step === STEP.review && (
        <div className="bg-gray-800 rounded-xl border border-gray-700 p-5 space-y-4">
          <div>
            <h2 className="text-sm font-semibold text-white">
              Review & Submit
            </h2>
            <p className="text-xs text-gray-400 mt-0.5">
              Add a note describing the unit/location and work done, then
              submit.
            </p>
          </div>
          <ReviewTable
            laborItems={reviewLaborItems}
            worker={effectiveWorkerName}
            project={selectedProject?.projectName || contextProjectName}
            workOrder={`${selectedWO?.woNumber ? selectedWO.woNumber + " — " : ""}${selectedWO?.title || ""}`}
            section={selectedSection?.displayName || selectedSection?.name}
            payPeriod={payPeriod}
            workDate={workDate}
            effectiveHstRate={effectiveHstRate}
          />
          <div>
            <label className="text-xs text-gray-400 uppercase tracking-wide mb-1.5 block">
              Notes <span className="text-red-400">*</span>
            </label>
            <p className="text-[11px] text-gray-600 mb-2">
              Include unit number and description of work (e.g. "Unit 412 —
              bedroom + hallway drywall")
            </p>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. Unit 412 — bedroom + hallway ceiling drywall"
              rows={3}
              className={cls(
                "w-full px-3 py-2 text-sm bg-gray-700 border rounded-lg text-gray-200 placeholder-gray-500 focus:outline-none resize-none",
                notes.trim().length === 0
                  ? "border-red-700/50 focus:border-red-500"
                  : "border-gray-600 focus:border-orange-500",
              )}
            />
            {notes.trim().length === 0 && (
              <p className="text-[11px] text-red-400 mt-1">
                Required before submitting.
              </p>
            )}
          </div>
        </div>
      )}

      {/* ── Navigation ────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between pt-1">
        <button
          onClick={() =>
            step === 0 ? goBackToProject() : setStep((s) => s - 1)
          }
          className="px-4 py-2 text-sm text-gray-300 bg-gray-700 border border-gray-600 rounded-lg hover:bg-gray-600 transition-colors"
        >
          {step === 0 ? "Cancel" : "← Back"}
        </button>
        {step < STEPS.length - 1 ? (
          <button
            onClick={handleNext}
            disabled={!canProceed}
            className="px-6 py-2 text-sm font-semibold text-white bg-orange-500 rounded-lg hover:bg-orange-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            Next →
          </button>
        ) : (
          <button
            onClick={handleSubmit}
            disabled={submitting || !notes.trim()}
            className="px-6 py-2 text-sm font-semibold text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-40 transition-colors"
          >
            {submitting ? "Submitting…" : "Submit Log"}
          </button>
        )}
      </div>
    </div>
  );
}
